import urls from "../../../routes";
import Page from "../../Page";
import styles from "./Wix.module.scss";
import Card from "../../../ui/Card/Card";
import SetupWixModal from "./SetupWixModal";
import { useNavigate } from "react-router-dom";
import { graphql } from "../../../__generated__/gql";
import { useMutation, useQuery } from "urql";
import { Controller, FormProvider, useForm } from "react-hook-form";
import Header from "../../Settings/components/Header/Header";
import Badge from "../../../ui/Badge/Badge";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Wix as WixLogo } from "../../../ui/LogoIcon/LogoIcon";
import Button from "../../../ui/Button/Button";
import SettingsSection from "../../Settings/components/SettingsSection";
import Select from "../../../ui/Select/Select";

const WixConnectionQuery = graphql(/* GraphQL */ `
  query wixConnection {
    wixConnection {
      id
      siteId
      siteName
      connected
      settings {
        publish
      }
    }
  }
`);

const DeleteWixConnectionMutation = graphql(/* GraphQL */ `
  mutation deleteWixConnection {
    deleteWixConnection {
      id
    }
  }
`);

const UpdateWixConnectionSettingsMutation = graphql(/* GraphQL */ `
  mutation updateWixConnectionSettings(
    $input: UpdateWixConnectionSettingsInput!
  ) {
    updateWixConnectionSettings(input: $input) {
      id
      siteId
      connected
      settings {
        publish
      }
    }
  }
`);

const schema = z.object({
  publish: z.boolean(),
});

type WixFormData = z.infer<typeof schema>;

function WixForm({
  connected,
  siteName,
  defaultValues,
}: {
  connected: boolean;
  siteName: string;
  defaultValues?: WixFormData;
}) {
  const navigate = useNavigate();
  const methods = useForm<WixFormData>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const [, deleteWixConnection] = useMutation(DeleteWixConnectionMutation);
  const [, updateWixConnectionSettings] = useMutation(
    UpdateWixConnectionSettingsMutation,
  );

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty, isValid },
    reset,
  } = methods;

  const onSubmit = async (data: WixFormData) => {
    await updateWixConnectionSettings({
      input: {
        publish: data.publish,
      },
    });
  };

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Header
              title="Wix"
              titleElement={
                connected ? (
                  <Badge color="success">Connected</Badge>
                ) : (
                  <Badge color="default">Not Connected</Badge>
                )
              }
              description={<>Setup publishing to Wix.</>}
              saveButtonText={connected ? "Save" : "Connect"}
              isSubmitting={isSubmitting}
              isDirty={isDirty}
              isDisabled={!isValid}
              onCancel={() => reset()}
            />
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                marginBottom: 20,
                paddingTop: 24,
                borderTop: "1px solid var(--color-gray-200)",
              }}
            >
              <Badge size="large" variant="badge" showDot={false}>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <WixLogo size="medium" />
                  <span
                    style={{ fontWeight: 600, color: "var(--color-gray-800)" }}
                  >
                    {siteName}
                  </span>
                </div>
              </Badge>
              <Button
                variant="link"
                onClick={async () => {
                  await deleteWixConnection({});
                  await navigate(urls.publishing.connections.root);
                }}
              >
                Disconnect
              </Button>
            </div>
            <SettingsSection
              label="Publish post"
              description="Whether or not articles should be published or saved as drafts"
            >
              <div className={styles.select}>
                <Controller
                  control={methods.control}
                  name="publish"
                  render={({ field }) => (
                    <Select
                      data={[
                        {
                          label: "Publish",
                          value: "true",
                        },
                        {
                          label: "Draft",
                          value: "false",
                        },
                      ]}
                      value={field.value ? "true" : "false"}
                      onChange={(e) => {
                        field.onChange(e === "true");
                      }}
                    />
                  )}
                />
              </div>
            </SettingsSection>
          </form>
        </FormProvider>
      </Card>
    </div>
  );
}

function Wix() {
  const navigate = useNavigate();

  const [wixConnectionResponse, refetch] = useQuery({
    query: WixConnectionQuery,
  });

  const loading = wixConnectionResponse.fetching;

  return (
    <Page
      loading={loading}
      title="Wix"
      documentTitle="Wix setup"
      breadcrumbs={[
        { url: urls.publishing.connections.root, name: "Publishing" },
      ]}
    >
      <SetupWixModal
        onClose={() => navigate(urls.publishing.connections.root)}
        onSubmit={() => refetch({ requestPolicy: "network-only" })}
        isOpen={!loading && !wixConnectionResponse.data?.wixConnection}
      />
      {wixConnectionResponse.data?.wixConnection && (
        <WixForm
          connected={!!wixConnectionResponse.data?.wixConnection.connected}
          siteName={wixConnectionResponse.data?.wixConnection.siteName}
          defaultValues={{
            publish:
              wixConnectionResponse.data?.wixConnection.settings?.publish ??
              false,
          }}
        />
      )}
    </Page>
  );
}

export default Wix;
